/**
 * API Execution Hook with MSAL Authentication
 *
 * Handles authenticated API calls with proper session expiry detection.
 *
 * Error Handling:
 * 1. MSAL Layer: Catches InteractionRequiredAuthError when B2C session expires
 * 2. HTTP Layer: Catches 401/403 responses from backend
 * 3. Both trigger centralized session expiry modal
 */

import { msalInstance } from '../index';
import { protectedResources } from '../config/msalConfig';
import { showSessionExpiredModal, isSessionExpiredError } from '../utils/sessionExpiryHandler';

const execute = async (method, endpoint, data = null) => {
    //console.log('Executing fetch with MSAL', method, endpoint, data);
    const account = msalInstance.getActiveAccount();
    if (!account) {
        throw Error('No active account found');
    }

    // Issue 5: Wrap MSAL token acquisition in try-catch
    // When B2C refresh token expires (24 hours for SPAs), acquireTokenSilent throws InteractionRequiredAuthError
    // Note: MSAL automatically refreshes access tokens (60 min) using refresh tokens
    let result;
    try {
        result = await msalInstance.acquireTokenSilent({
            scopes: protectedResources.api.scopes.read,
            account: account,
            redirectUri: '/',
        });
    } catch (msalError) {
        // Check if this is a session expiry error
        if (isSessionExpiredError(msalError)) {
            console.error('[Execute] MSAL session expired:', msalError.errorCode || msalError.name);
            showSessionExpiredModal();
        }
        throw msalError;
    }

    try {
        const headers = new Headers();
        const bearer = `Bearer ${result.accessToken}`;
        headers.append("Authorization", bearer);

        if (data) headers.append('Content-Type', 'application/json');

        const options = {
            method: method,
            headers: headers,
            body: data ? JSON.stringify(data) : null,
        };

        const fetchResponse = await fetch(endpoint, options);

        // Issue 5: Check HTTP status BEFORE parsing JSON
        // Backend returns 401/403 when token is expired or invalid
        if (fetchResponse.status === 401 || fetchResponse.status === 403) {
            console.error('[Execute] Backend returned 401/403 - session expired');
            showSessionExpiredModal();
            throw new Error('Session expired. Please log in again.');
        }

        // Check for other HTTP errors before parsing JSON
        if (!fetchResponse.ok) {
            // Try to parse as JSON first to get structured error info
            let errorData;
            const contentType = fetchResponse.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
                try {
                    errorData = await fetchResponse.json();
                } catch (e) {
                    errorData = { message: await fetchResponse.text() };
                }
            } else {
                errorData = { message: await fetchResponse.text() };
            }

            const error = new Error(errorData.message || 'Request failed');
            error.status = fetchResponse.status;
            error.errorType = errorData.errorType; // Pass through errorType from backend
            error.response = {
                status: fetchResponse.status,
                data: errorData
            };
            throw error;
        }

        // NOW safe to parse JSON (after validating status)
        const jsonResponse = await fetchResponse.json();
        return jsonResponse;

    } catch (e) {
        throw e;
    }
};

export default execute;